import { sessionStore } from '@features/auth/services/sessionStore'
import { getMe, type MeProfileWire } from '../api/accountApi'

/**
 * `account profile` の in-memory cache (display name 用)。
 *
 * HomePage の greeting と Sidenav footer の両方が同じ display name を必要とするため、
 * 個別に `useAccountProfile()` を呼ぶと GET /users/me が重複発火する。本 store は
 * 最初の subscribe で 1 度だけ fetch し、profile を共有する。
 *
 * 保持するのは `name` と `email` のみ (sensitive ではない)。サインアウト時に
 * clear() を呼ぶ必要がある。
 */

interface CachedProfile {
  email: string
  name: string | null
}

type Listener = () => void

let cached: CachedProfile | null = null
let pending: Promise<void> | null = null
const listeners = new Set<Listener>()

// UNAUTH に落ちたら自動で cache を捨てる (logout / clear 全経路に対応)。
sessionStore.subscribe(() => {
  if (sessionStore.getState().kind === 'unauth' && cached !== null) {
    cached = null
    pending = null
    for (const l of listeners) l()
  }
})

const emit = (): void => {
  for (const l of listeners) l()
}

const fetchNow = async (): Promise<void> => {
  try {
    const wire: MeProfileWire = await getMe()

    cached = { email: wire.email, name: wire.name }
    emit()
  } catch {
    // 失敗時は cache を更新しない (email local part fallback がそのまま動く)
  } finally {
    pending = null
  }
}

const ensureLoaded = (): void => {
  if (cached !== null || pending !== null) return
  if (sessionStore.getState().kind !== 'unlocked') return
  pending = fetchNow()
}

const emailLocalPart = (email: string): string => email.split('@')[0] ?? email

export const accountProfileStore = {
  /**
   * `name` が設定されていればそれを、無ければ email の `@` 前を返す。
   * email も無ければ空文字。
   */
  getDisplayName(): string {
    ensureLoaded()

    const profile = cached
    if (profile === null) {
      const session = sessionStore.getState()
      if (session.kind === 'unlocked') return emailLocalPart(session.material.email)

      return ''
    }
    if (profile.name && profile.name.trim().length > 0) return profile.name

    return emailLocalPart(profile.email)
  },

  /** PATCH /me を成功させた hook (AccountSection 等) から呼んで cache を更新する。 */
  updateName(name: string | null): void {
    if (cached === null) return
    cached = { ...cached, name }
    emit()
  },

  subscribe(cb: Listener): () => void {
    listeners.add(cb)
    ensureLoaded()

    return () => {
      listeners.delete(cb)
    }
  },

  /** ログアウト時に呼ぶ — 次の subscribe で再取得される。 */
  clear(): void {
    cached = null
    pending = null
    emit()
  },
}
